import React, { useEffect, useRef, useState } from 'react';
import SearchWidget from './search_widget';
import TracksWidget from './tracks_widget';
import PlaylistModal from './playlist_modal';
import PersonaModal from './persona_modal';
import CatalogBrowseModal from './catalog_browse_modal';
import AllTracksModal from './all_tracks_modal';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faUser,
  faListUl,
  faRandom,
  faGlobe,
  faGuitar,
  faDatabase,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import AppMoreMenu from '../common/app_more_menu';
import { useMusicPlayer, SKIPPED_PLAYLIST_NAME, isSkippedPlaylist, PLAYING_SESSION_DETAIL_ID } from './music_player_context';

const server = axios.create({ baseURL: '/api' });

const MusicApp = () => {
  const tracksRef = useRef(null);
  const searchRef = useRef(null);
  const [showPlaylists, setShowPlaylists] = useState(false);
  const [showPersonas, setShowPersonas] = useState(false);
  const [showAllTracks, setShowAllTracks] = useState(false);
  const [browseKind, setBrowseKind] = useState(null); // languages | genres | artists
  const {
    allTracks,
    tags,
    playlists,
    setPlaylists,
    displayQueue,
    playTrack,
    loadingTrackId,
    activePlaylist,
    queueSources,
    activePlaylistIds,
    playPlaylist,
    playBrowse,
    playTrackFromBrowse,
    playSearchResults,
    deleteSearchResults,
    playTrackFromPlaylist,
    removeTrackFromPlaylist,
    removeQueueSource,
    handleSearchTrackClick,
    handleTrackClick,
    playSharedTrack,
    addTrackToCurrentList,
    playTrackNow,
    handleDeleteTrack,
    requestAddTrack,
    handleTrackInfoUpdated,
    isTrackSkipped,
    addTracksToSkipped,
    removeTracksFromSkipped,
    clearSkippedTracks,
    skipPlaylistTracks,
    keepPlaylistTracks,
    registerTracksWidget,
    registerYtMount,
    personas,
    activePersonas,
    playPersonas,
    renamePersona,
    createPersona,
    deletePersona,
    addTracksToPersona,
    removeTrackFromPersona,
    suggestTracksForPersona,
    shuffleMode,
    handleShuffle,
    clearActivePlaylist,
    clearPersonaPlay,
  } = useMusicPlayer();

  const queueAppendMode = queueSources.length > 0 || activePersonas.length > 0;
  const isAggregateUser = String(localStorage.getItem('walmates-login_id') || '').toLowerCase() === 'walmates';
  const [playlistOpenId, setPlaylistOpenId] = useState(null);
  const [browseOpenKey, setBrowseOpenKey] = useState(null);

  const openPlaylistSheet = (detailId = null) => {
    setPlaylistOpenId(detailId);
    setShowPlaylists(true);
  };

  const openPlayingList = () => {
    if (!activePlaylist) return;
    if (activePlaylist.isPersona) {
      setShowPersonas(true);
      return;
    }
    if (queueSources.length > 0) {
      openPlaylistSheet(PLAYING_SESSION_DETAIL_ID);
    }
  };

  useEffect(() => {
    registerTracksWidget(tracksRef);
    return () => registerTracksWidget(null);
  }, [registerTracksWidget]);

  // One-time cleanup of old auto-created language playlists.
  useEffect(() => {
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    if (!token || !user_id) return;
    const flag = `music-lang-playlist-cleanup:${user_id}`;
    if (localStorage.getItem(flag)) return;
    server
      .post('/music/library/cleanup-language-playlists', { user_id: Number(user_id) }, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const removed = res.data?.removed || [];
        if (removed.length) {
          const removedIds = new Set(removed.map((r) => r.id));
          setPlaylists((prev) => prev.filter((p) => !removedIds.has(p.id)));
        }
        localStorage.setItem(flag, '1');
      })
      .catch(() => {
        // Non-fatal — user can still browse Languages from catalog.
      });
  }, [setPlaylists]);

  return (
    <div className="music-app">
      <header className="music-header">
        <h1 className="music-brand">Music</h1>
        <button
          type="button"
          className="music-search-launch"
          onClick={() => searchRef.current?.open()}
          aria-label="Search title or artist"
        >
          <FontAwesomeIcon icon={faSearch} />
          <span>Search title or artist…</span>
        </button>
        <div className="music-header-actions">
          <AppMoreMenu
            buttonClassName="music-header-btn"
            title="More"
            items={[
              ...(!isAggregateUser ? [{
                label: 'All Tracks',
                icon: faDatabase,
                onClick: () => setShowAllTracks(true),
              }] : []),
              {
                label: 'Playlist',
                icon: faListUl,
                onClick: () => openPlaylistSheet(null),
              },
              {
                label: 'Languages',
                icon: faGlobe,
                onClick: () => {
                  setBrowseOpenKey(null);
                  setBrowseKind('languages');
                },
              },
              {
                label: 'Genre',
                icon: faGuitar,
                onClick: () => {
                  setBrowseOpenKey(null);
                  setBrowseKind('genres');
                },
              },
              {
                label: 'Artist',
                icon: faUser,
                onClick: () => {
                  setBrowseOpenKey(null);
                  setBrowseKind('artists');
                },
              },
            ]}
          />
          <button
            type="button"
            className={`music-header-btn${shuffleMode ? ' is-active' : ''}`}
            onClick={handleShuffle}
            title={shuffleMode ? 'Shuffle on' : 'Shuffle off'}
            aria-label="Shuffle"
            aria-pressed={shuffleMode}
          >
            <FontAwesomeIcon icon={faRandom} />
          </button>
        </div>
      </header>

      <SearchWidget
        ref={searchRef}
        tracks={allTracks}
        onTrackClick={handleSearchTrackClick}
        onAddToQueue={addTrackToCurrentList}
        appendMode={queueAppendMode}
        onPlaySearchResults={playSearchResults}
        onDeleteSearchResults={deleteSearchResults}
        requestAddTrack={requestAddTrack}
      />

      <div className="music-app-scroll">
        <div className="music-yt-mount" ref={registerYtMount} />

        <main className="music-library">
          <p className="music-library-title">Your library</p>
          {activePlaylist && (
            <div className="music-active-list-banner">
              <button
                type="button"
                className="music-active-list-main"
                onClick={openPlayingList}
                aria-label={`Open ${activePlaylist.name}`}
              >
                <span className={`music-active-list-art${activePlaylist.isPersona ? ' is-persona' : ''}`}>
                  {activePlaylist.isPersona ? (
                    <span>{activePlaylist.emoji || '🎧'}</span>
                  ) : activePlaylist.coverUrl ? (
                    <img src={activePlaylist.coverUrl} alt="" />
                  ) : (
                    <FontAwesomeIcon icon={faListUl} />
                  )}
                </span>
                <span className="music-active-list-meta">
                  <span className="music-active-list-name">
                    {activePlaylist.name}
                    {activePlaylist.extraSources > 0 ? ` +${activePlaylist.extraSources}` : ''}
                  </span>
                  <span className="music-active-list-sub">
                    {activePlaylist.label || 'List'}
                    {activePlaylist.trackCount != null ? ` · ${activePlaylist.trackCount} tracks` : ''}
                  </span>
                </span>
              </button>
              <button
                type="button"
                className="music-active-list-close"
                onClick={() => {
                  if (activePlaylist.isPersona) clearPersonaPlay();
                  else clearActivePlaylist();
                }}
                aria-label="Stop playing this list"
                title="Stop playing this list"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          )}
          <TracksWidget
            ref={tracksRef}
            tracks={displayQueue}
            onTrackClick={handleTrackClick}
            onAddToQueue={addTrackToCurrentList}
            appendMode={queueAppendMode}
            onTrackDelete={handleDeleteTrack}
            tags={tags}
            playTrack={playTrack}
            loadingTrackId={loadingTrackId}
            playlists={playlists}
            setPlaylists={setPlaylists}
            onTrackInfoUpdated={handleTrackInfoUpdated}
            isTrackSkipped={isTrackSkipped}
            onSkipTrack={(track) => {
              const id = track?.video_id || track?.id;
              if (id) addTracksToSkipped([id]);
            }}
            onKeepTrack={(track) => {
              const id = track?.video_id || track?.id;
              if (id) removeTracksFromSkipped([id]);
            }}
            onAddToPlaylist={(trackId, playlistName) => {
              const token = localStorage.getItem('walmates-token');
              const user_id = localStorage.getItem('walmates-user_id');
              const trimmed = String(playlistName || '').trim();
              if (!trimmed) return;
              if (trimmed.localeCompare(SKIPPED_PLAYLIST_NAME, undefined, { sensitivity: 'accent' }) === 0) {
                return;
              }
              const playlist = playlists.find((p) => p.name === trimmed);
              if (playlist && isSkippedPlaylist(playlist)) return;

              const toggleMembership = (pl) => {
                const isAlreadyIn = pl.trackIds?.includes(trackId);
                return server.post('/music/playlist/modify', {
                  playlist_id: pl.id,
                  add: isAlreadyIn ? [] : [trackId],
                  remove: isAlreadyIn ? [trackId] : [],
                }, {
                  headers: { Authorization: `Bearer ${token}` },
                }).then(() => {
                  setPlaylists((prev) =>
                    prev.map((p) => {
                      if (p.id !== pl.id) return p;
                      let updatedIds = p.trackIds || [];
                      if (isAlreadyIn) {
                        updatedIds = updatedIds.filter((id) => id !== trackId);
                      } else {
                        updatedIds = [...updatedIds, trackId];
                      }
                      return { ...p, trackIds: updatedIds };
                    })
                  );
                });
              };

              if (!playlist) {
                const timestamp = Math.floor(Date.now() / 1000);
                server.post('/music/playlist/create', {
                  user_id,
                  name: trimmed,
                  last_modified: timestamp,
                }, {
                  headers: { Authorization: `Bearer ${token}` },
                }).then((res) => {
                  const newPl = {
                    name: trimmed,
                    trackIds: [trackId],
                    id: res.data.id,
                    lastModified: res.data.last_modified,
                    synced: true,
                  };
                  setPlaylists((prev) => [...prev, newPl]);
                  return server.post('/music/playlist/modify', {
                    playlist_id: newPl.id,
                    add: [trackId],
                    remove: [],
                  }, { headers: { Authorization: `Bearer ${token}` } });
                }).catch(console.error);
                return;
              }

              toggleMembership(playlist).catch(console.error);
            }}
          />
        </main>
      </div>

      <PersonaModal
        open={showPersonas}
        personas={personas}
        playlists={playlists}
        allTracks={allTracks}
        activePersonaIds={activePersonas.map((p) => p.id)}
        appendMode={queueAppendMode}
        onPlayPersonas={playPersonas}
        onRenamePersona={renamePersona}
        onCreatePersona={createPersona}
        onDeletePersona={deletePersona}
        onAddTracks={addTracksToPersona}
        onRemoveTrack={removeTrackFromPersona}
        onSuggestTracks={suggestTracksForPersona}
        onPlayLibraryTrack={(track) => {
          const idx = allTracks.findIndex((t) => t.video_id === track.video_id);
          handleTrackClick(idx >= 0 ? idx : 0, track);
        }}
        onAddSuggestedTrack={(track) => {
          requestAddTrack(track);
        }}
        onClose={() => setShowPersonas(false)}
      />

      <PlaylistModal
        open={showPlaylists}
        initialDetailId={playlistOpenId}
        playlists={playlists}
        allTracks={allTracks}
        activePlaylistIds={activePlaylistIds}
        appendMode={queueAppendMode}
        playTrack={playTrack}
        loadingTrackId={loadingTrackId}
        isTrackSkipped={isTrackSkipped}
        onPlayPlaylist={playPlaylist}
        onPlayTrackFromPlaylist={playTrackFromPlaylist}
        onPlaySessionTrack={playTrackNow}
        onAddTrackToQueue={addTrackToCurrentList}
        onRemoveTrackFromPlaylist={removeTrackFromPlaylist}
        onRemoveQueueSource={removeQueueSource}
        queueSources={queueSources}
        onSkipPlaylist={skipPlaylistTracks}
        onKeepPlaylist={keepPlaylistTracks}
        onClearSkipped={clearSkippedTracks}
        onClose={() => {
          setShowPlaylists(false);
          setPlaylistOpenId(null);
        }}
      />

      <CatalogBrowseModal
        open={browseKind != null}
        kind={browseKind || 'languages'}
        initialKey={browseOpenKey}
        queueSources={queueSources}
        appendMode={queueAppendMode}
        playTrack={playTrack}
        loadingTrackId={loadingTrackId}
        isTrackSkipped={isTrackSkipped}
        onPlayBrowse={playBrowse}
        onPlayTrackFromBrowse={playTrackFromBrowse}
        onAddTrackToQueue={addTrackToCurrentList}
        onRemoveQueueSource={removeQueueSource}
        onClose={() => {
          setBrowseKind(null);
          setBrowseOpenKey(null);
        }}
      />

      <AllTracksModal
        open={showAllTracks}
        onClose={() => setShowAllTracks(false)}
        onPlay={playSharedTrack}
        onAdd={requestAddTrack}
      />
    </div>
  );
};

export default MusicApp;
